<script setup>
import { ref, computed } from 'vue';

const props = defineProps({
 steps: {
 type: Array,
 default: () => [],
 },
 fields: {
 type: Array,
 default: () => [],
 },
});

const emit = defineEmits(['update:steps']);

const editingStepId = ref(null);
const editingName = ref('');
const showBranchEditor = ref(null);

function generateStepId() {
 return `step_${Date.now()}_${Math.random().toString(36).substr(2, 4)}`;
}

function addStep() {
 const newStep = {
 id: generateStepId(),
 name: `Etapa ${props.steps.length + 1}`,
 fields: [],
 branch_rules: [],
 };
 const updated = [...props.steps, newStep];
 emit('update:steps', updated);
}

function removeStep(index) {
 if (props.steps.length <= 1) return;
 const updated = [...props.steps];
 updated.splice(index, 1);
 emit('update:steps', updated);
}

function startEditing(step) {
 editingStepId.value = step.id;
 editingName.value = step.name;
}

function finishEditing(index) {
 if (!editingName.value.trim()) {
 editingStepId.value = null;
 return;
 }
 const updated = [...props.steps];
 updated[index] = { ...updated[index], name: editingName.value.trim() };
 emit('update:steps', updated);
 editingStepId.value = null;
}

function moveStep(index, direction) {
 const newIndex = index + direction;
 if (newIndex < 0 || newIndex >= props.steps.length) return;
 const updated = [...props.steps];
 const [moved] = updated.splice(index, 1);
 updated.splice(newIndex, 0, moved);
 emit('update:steps', updated);
}

function toggleBranchEditor(stepId) {
 showBranchEditor.value =
 showBranchEditor.value === stepId ? null : stepId;
}

function addBranchRule(stepIndex) {
 const updated = [...props.steps];
 if (!updated[stepIndex].branch_rules) {
 updated[stepIndex].branch_rules = [];
 }
 updated[stepIndex].branch_rules.push({
 field_id: '',
 operator: 'equals',
 value: '',
 goto_step_id: '',
 });
 emit('update:steps', updated);
}

function removeBranchRule(stepIndex, ruleIndex) {
 const updated = [...props.steps];
 updated[stepIndex].branch_rules.splice(ruleIndex, 1);
 emit('update:steps', updated);
}

function updateBranchRule(stepIndex, ruleIndex, key, value) {
 const updated = [...props.steps];
 updated[stepIndex].branch_rules[ruleIndex] = {
 ...updated[stepIndex].branch_rules[ruleIndex],
 [key]: value,
 };
 emit('update:steps', updated);
}

const operators = [
 { value: 'equals', label: 'Igual a' },
 { value: 'not_equals', label: 'Diferente de' },
 { value: 'contains', label: 'Contem' },
 { value: 'not_empty', label: 'Nao vazio' },
 { value: 'is_empty', label: 'Vazio' },
];
</script>

<template>
 <div class="py-3">
 <div class="px-4 mb-3 flex items-center justify-between">
 <h3
 class="text-xs font-semibold text-n-slate-10 uppercase tracking-wider"
 >
 Etapas
 </h3>
 <button
 class="flex items-center gap-1 text-xs font-medium text-woot-600 dark:text-woot-400 hover:text-woot-700 dark:hover:text-woot-300 transition-colors"
 @click="addStep"
 >
 <span class="i-lucide-plus size-3" />
 Adicionar
 </button>
 </div>

 <div class="px-3 space-y-1">
 <div
 v-for="(step, index) in steps"
 :key="step.id"
 class="rounded-lg border border-n-weak bg-n-solid-1"
 >
 <!-- Step header -->
 <div class="flex items-center gap-2 p-2">
 <!-- Reorder controls -->
 <div class="flex flex-col gap-0.5">
 <button
 :disabled="index === 0"
 class="flex items-center justify-center size-4 rounded text-n-slate-9 hover:text-n-slate-11 dark:hover:text-n-slate-7 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
 @click="moveStep(index, -1)"
 >
 <span class="i-lucide-chevron-up size-3" />
 </button>
 <button
 :disabled="index === steps.length - 1"
 class="flex items-center justify-center size-4 rounded text-n-slate-9 hover:text-n-slate-11 dark:hover:text-n-slate-7 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
 @click="moveStep(index, 1)"
 >
 <span class="i-lucide-chevron-down size-3" />
 </button>
 </div>

 <!-- Step number -->
 <span
 class="flex items-center justify-center size-5 rounded-full bg-woot-100 dark:bg-woot-500/20 text-[10px] font-bold text-woot-700 dark:text-woot-400 shrink-0"
 >
 {{ index + 1 }}
 </span>

 <!-- Name (editable) -->
 <div class="flex-1 min-w-0">
 <input
 v-if="editingStepId === step.id"
 v-model="editingName"
 type="text"
 class="reset-base w-full h-6 px-1 text-xs font-medium rounded border border-woot-300 dark:border-woot-500 bg-n-solid-1 text-n-slate-12 focus:outline-none"
 @blur="finishEditing(index)"
 @keydown.enter="finishEditing(index)"
 @keydown.escape="editingStepId = null"
 />
 <button
 v-else
 class="text-xs font-medium text-n-slate-11 truncate block w-full text-left hover:text-woot-600 dark:hover:text-woot-400 transition-colors"
 @dblclick="startEditing(step)"
 >
 {{ step.name }}
 </button>
 </div>

 <!-- Actions -->
 <div class="flex items-center gap-0.5 shrink-0">
 <button
 class="flex items-center justify-center size-6 rounded text-n-slate-9 hover:text-woot-600 hover:bg-woot-50 dark:hover:bg-woot-500/10 dark:hover:text-woot-400 transition-colors"
 title="Regras de ramificação"
 @click="toggleBranchEditor(step.id)"
 >
 <span class="i-lucide-git-branch size-3" />
 </button>
 <button
 v-if="steps.length > 1"
 class="flex items-center justify-center size-6 rounded text-n-slate-9 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-500/10 dark:hover:text-red-400 transition-colors"
 title="Remover etapa"
 @click="removeStep(index)"
 >
 <span class="i-lucide-trash-2 size-3" />
 </button>
 </div>
 </div>

 <!-- Branch rules editor -->
 <div
 v-if="showBranchEditor === step.id"
 class="border-t border-n-weak p-2 bg-n-slate-2/50 dark:bg-n-slate-12/30"
 >
 <div class="flex items-center justify-between mb-2">
 <span
 class="text-[10px] font-semibold text-n-slate-10 uppercase tracking-wider"
 >
 Regras de Ramificação
 </span>
 <button
 class="text-[10px] font-medium text-woot-600 dark:text-woot-400 hover:text-woot-700 transition-colors"
 @click="addBranchRule(index)"
 >
 + Regra
 </button>
 </div>

 <div
 v-if="
 !step.branch_rules || step.branch_rules.length === 0
 "
 class="text-[10px] text-n-slate-9 text-center py-2"
 >
 Nenhuma regra. Irá para a próxima etapa por padrão.
 </div>

 <div
 v-for="(rule, ruleIdx) in step.branch_rules || []"
 :key="ruleIdx"
 class="flex flex-col gap-1.5 p-2 mb-1.5 bg-n-solid-1 rounded-lg border border-n-weak"
 >
 <div class="flex items-center gap-1">
 <span
 class="text-[10px] text-n-slate-9 shrink-0"
 >
 Se
 </span>
 <select
 :value="rule.field_id"
 class="reset-base flex-1 h-6 px-1.5 text-[10px] rounded border border-n-weak bg-n-solid-1 text-n-slate-12"
 @change="
 updateBranchRule(
 index,
 ruleIdx,
 'field_id',
 $event.target.value
 )
 "
 >
 <option value="">Campo...</option>
 <option
 v-for="f in fields"
 :key="f.id"
 :value="f.id"
 >
 {{ f.label }}
 </option>
 </select>
 </div>
 <div class="flex items-center gap-1">
 <select
 :value="rule.operator"
 class="reset-base flex-1 h-6 px-1.5 text-[10px] rounded border border-n-weak bg-n-solid-1 text-n-slate-12"
 @change="
 updateBranchRule(
 index,
 ruleIdx,
 'operator',
 $event.target.value
 )
 "
 >
 <option
 v-for="op in operators"
 :key="op.value"
 :value="op.value"
 >
 {{ op.label }}
 </option>
 </select>
 <input
 v-if="
 !['not_empty', 'is_empty'].includes(
 rule.operator
 )
 "
 :value="rule.value"
 type="text"
 placeholder="Valor..."
 class="reset-base flex-1 h-6 px-1.5 text-[10px] rounded border border-n-weak bg-n-solid-1 text-n-slate-12"
 @input="
 updateBranchRule(
 index,
 ruleIdx,
 'value',
 $event.target.value
 )
 "
 />
 </div>
 <div class="flex items-center gap-1">
 <span
 class="text-[10px] text-n-slate-9 shrink-0"
 >
 Ir para
 </span>
 <select
 :value="rule.goto_step_id"
 class="reset-base flex-1 h-6 px-1.5 text-[10px] rounded border border-n-weak bg-n-solid-1 text-n-slate-12"
 @change="
 updateBranchRule(
 index,
 ruleIdx,
 'goto_step_id',
 $event.target.value
 )
 "
 >
 <option value="">Etapa...</option>
 <option
 v-for="s in steps"
 :key="s.id"
 :value="s.id"
 >
 {{ s.name }}
 </option>
 </select>
 <button
 class="flex items-center justify-center size-5 rounded text-n-slate-9 hover:text-red-500 transition-colors shrink-0"
 @click="removeBranchRule(index, ruleIdx)"
 >
 <span class="i-lucide-x size-3" />
 </button>
 </div>
 </div>
 </div>
 </div>
 </div>
 </div>
</template>
